import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { getMenu, type LunchMenuCatalog, type MenuResponse } from "../api/menu";
import type {
  Item,
  Category,
  Allergen,
  Tag,
  Station,
  Vat,
  Addon,
  Modifier,
} from "../api/menu";
import { getRestaurantName } from "../lib/restaurant";
import {
  activeLunchMenuKey,
  isLunchCategory,
  isLunchItem,
  nextLunchBoundaryAt,
  withLunchMenus,
} from "../lib/lunchCatalog";
import { bumpLunchClock, lunchNow, useLunchClockTick } from "../lib/lunchClock";

/**
 * Обхват на каталога за компонентите отдолу: "full" (по подразбиране) — с
 * обедните групи (виж lib/lunchCatalog.ts); "regular" — само основното меню.
 * Редакторът на обедно меню обвива каталога си в "regular", за да избира от
 * истинските продукти, без да пипа ItemCatalog/CategoryChips.
 */
export const CatalogScopeContext = createContext<"full" | "regular">("full");

interface MenuData {
  items: Item[];
  categories: Category[];
  allergens: Allergen[];
  tags: Tag[];
  stations: Station[];
  vats: Vat[];
  addons: Addon[];
  modifiers: Modifier[];
  // Целият график (и неактивните) — за часовника в useLunchCatalogRefresh
  lunchMenus?: LunchMenuCatalog[];
}

const emptyMenu: MenuData = {
  items: [],
  categories: [],
  allergens: [],
  tags: [],
  stations: [],
  vats: [],
  addons: [],
  modifiers: [],
};

export const useMenu = () => {
  // Нов тик на обедния часовник → нов `select` → react-query преизчислява
  // активните обедни групи без заявка (работи и без интернет)
  const lunchClockTick = useLunchClockTick();
  const select = useCallback(
    (data: MenuResponse | undefined): MenuData =>
      data ? withLunchMenus(data, lunchNow()) : emptyMenu,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- тикът е сигналът
    [lunchClockTick],
  );
  return useQuery({
    queryKey: MENU_QUERY_KEY,
    queryFn: fetchMenu,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select,
  });
};

const MENU_QUERY_KEY = ["menu"];
const fetchMenu = () => getMenu(getRestaurantName());

/**
 * Проверка „още ли е активно обедното меню" по действие на сервитьора —
 * отваряне на масите, клик между масите, „Добави", „Поръчай пак". Веднага
 * преизчислява обедните групи по часовника (и офлайн), и пита облака за
 * свежо меню — ако друг терминал е приключил/изтрил обедното, групата
 * изчезва, а кошницата минава на основната цена (useEndedLunchCartItems).
 * `maxAgeMs` — колко старо меню още е „свежо" (0 = винаги питай).
 * Грешка (няма връзка) не спира нищо — остава кешираното меню.
 */
export const useLunchMenuCheck = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (maxAgeMs: number) => {
      bumpLunchClock();
      await queryClient
        .fetchQuery({ queryKey: MENU_QUERY_KEY, queryFn: fetchMenu, staleTime: maxAgeMs })
        .then(() => bumpLunchClock())
        .catch(() => undefined);
    },
    [queryClient],
  );
};

// setTimeout държи най-много ~24.8 дни
const MAX_TIMER_MS = 2_147_483_647;
// Предпазна проверка: заспал таб/забавен таймер не бива да остави групата
const LUNCH_SAFETY_CHECK_MS = 30_000;

/**
 * Показва/скрива групата „Обедно меню" точно в началния и крайния час по
 * часовника на терминала — и без интернет. В граничния момент преизчислява
 * каталога локално (`bumpLunchClock`) и пита облака за свежо меню; ако
 * облакът не отговаря, касата връща старото копие, а графикът в него пак
 * скрива изтеклото меню. Вика се веднъж, в „Маркиране".
 */
export const useLunchCatalogRefresh = () => {
  const queryClient = useQueryClient();
  const { data } = useMenu();
  const lunchMenus = data?.lunchMenus;
  // Всяко преизчисляване (граница, предпазна проверка) пренавива таймера за
  // следващата граница — иначе само първата е точна
  const clockTick = useLunchClockTick();

  useEffect(() => {
    const schedules = lunchMenus ?? [];
    if (schedules.length === 0) return;
    let shownKey = activeLunchMenuKey(schedules, lunchNow());
    const refresh = () => {
      shownKey = activeLunchMenuKey(schedules, lunchNow());
      bumpLunchClock();
      queryClient.invalidateQueries({ queryKey: MENU_QUERY_KEY });
    };

    const now = lunchNow();
    const boundary = nextLunchBoundaryAt(schedules, now);
    // Точно в граничната секунда (+50 ms, за да сме сигурно след нея);
    // сървърът прощава няколко секунди на заявките по пътя
    const delay = boundary ? boundary.getTime() - now.getTime() + 50 : null;
    const timer =
      delay != null && delay <= MAX_TIMER_MS
        ? window.setTimeout(refresh, Math.max(0, delay))
        : undefined;
    const safety = window.setInterval(() => {
      if (activeLunchMenuKey(schedules, lunchNow()) !== shownKey) refresh();
    }, LUNCH_SAFETY_CHECK_MS);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(safety);
    };
  }, [lunchMenus, clockTick, queryClient]);
};

// Derived selectors as hooks

export const useMenuItems = () => {
  const { data } = useMenu();
  const scope = useContext(CatalogScopeContext);
  return useMemo(() => {
    const items = data?.items ?? [];
    return scope === "regular" ? items.filter((item) => !isLunchItem(item)) : items;
  }, [data, scope]);
};

export const useEnabledItems = () => {
  const items = useMenuItems();
  return useMemo(() => items.filter((item) => item.enabled), [items]);
};

export const useMenuCategories = () => {
  const { data } = useMenu();
  const scope = useContext(CatalogScopeContext);
  return useMemo(() => {
    const categories = data?.categories ?? [];
    return scope === "regular"
      ? categories.filter((category) => !isLunchCategory(category))
      : categories;
  }, [data, scope]);
};

export const useActiveCategories = () => {
  const categories = useMenuCategories();
  return useMemo(() => categories.filter((cat) => cat.isActive), [categories]);
};

export const useMenuAllergens = () => {
  const { data } = useMenu();
  return data?.allergens ?? [];
};

export const useMenuTags = () => {
  const { data } = useMenu();
  return data?.tags ?? [];
};

export const useMenuStations = () => {
  const { data } = useMenu();
  return data?.stations ?? [];
};

export const useMenuVats = () => {
  const { data } = useMenu();
  return data?.vats ?? [];
};

export const useMenuAddons = () => {
  const { data } = useMenu();
  return data?.addons ?? [];
};

export const useMenuModifiers = () => {
  const { data } = useMenu();
  return data?.modifiers ?? [];
};

// Подредба по sortIndex; записите БЕЗ sortIndex отиват най-отзад
const bySortIndex = (a: number | null, b: number | null) =>
  (a ?? Number.MAX_SAFE_INTEGER) - (b ?? Number.MAX_SAFE_INTEGER);

export const useItemsGroupedByCategory = (categoryId: number | null) => {
  const items = useEnabledItems();
  const categories = useMenuCategories();

  return useMemo(() => {
    // Групите — по sortIndex на категорията; продуктите в група — по своя
    // sortIndex (без sortIndex → най-отзад). Групата БЕЗ категория
    // (categoryId null — продаваеми без група, „Без група" в каталога) е
    // ВИНАГИ най-отдолу, и след категориите без sortIndex.
    const toSortedGroups = (grouped: Map<number | null, Item[]>) =>
      Array.from(grouped.entries())
        .map(([catId, catItems]) => ({
          categoryId: catId,
          category:
            catId != null
              ? (categories.find((c) => c.id === catId) ?? null)
              : null,
          items: [...catItems].sort((a, b) =>
            bySortIndex(a.sortIndex, b.sortIndex),
          ),
        }))
        .sort((a, b) => {
          const aNoGroup = a.categoryId === null;
          const bNoGroup = b.categoryId === null;
          if (aNoGroup !== bNoGroup) return aNoGroup ? 1 : -1;
          return bySortIndex(
            a.category?.sortIndex ?? null,
            b.category?.sortIndex ?? null,
          );
        });

    if (categoryId === null) {
      // Group all items by their category
      const grouped = new Map<number | null, Item[]>();
      items.forEach((item) => {
        const catId = item.categoryId;
        if (!grouped.has(catId)) {
          grouped.set(catId, []);
        }
        grouped.get(catId)!.push(item);
      });

      return toSortedGroups(grouped);
    }

    // Get all descendant category IDs
    const getDescendantIds = (parentId: number): number[] => {
      const children = categories.filter((c) => c.parentId === parentId);
      return children.reduce(
        (acc, child) => [...acc, child.id, ...getDescendantIds(child.id)],
        [] as number[],
      );
    };

    const relevantCategoryIds = [categoryId, ...getDescendantIds(categoryId)];

    const filteredItems = items.filter(
      (item) =>
        item.categoryId != null &&
        relevantCategoryIds.includes(item.categoryId),
    );

    // Group by category
    const grouped = new Map<number | null, Item[]>();
    filteredItems.forEach((item) => {
      const catId = item.categoryId;
      if (!grouped.has(catId)) {
        grouped.set(catId, []);
      }
      grouped.get(catId)!.push(item);
    });

    return toSortedGroups(grouped);
  }, [items, categories, categoryId]);
};
